import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useNotify } from '@newfront-insurance/next-notifications';

import { OutlookMailboxSettings } from './outlook-mailbox-settings';
import { SettingsCard } from './settings-card';
import { useSettingsPanelContext } from './settings-panel-context';
import { MSGraphToggle } from '../../ms-graph';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useMSGraphAuth } from '../contexts/ms-graph-context';
import { useChatSettings } from '../hooks/use-chat-settings';

function OutlookManageView({ onBack }: { onBack: () => void }) {
  const msGraphAuth = useMSGraphAuth();
  const graphClient = msGraphAuth?.graphClient ?? null;
  const { serverSettings, persistSettings } = useChatOverlayContext();
  const { backHandlerRef } = useSettingsPanelContext();
  const notify = useNotify();

  const chatSettings = useChatSettings({
    graphClient,
    serverSelectedGroupIds: serverSettings?.outlookConnectedEmailGroupIds,
    serverSharedInboxes: serverSettings?.outlookSharedInboxes,
    onSave: ({ groupIds, sharedInboxes }) =>
      persistSettings({ outlookConnectedEmailGroupIds: groupIds, outlookSharedInboxes: sharedInboxes }),
  });

  return (
    <div className="flex h-full flex-col">
      <OutlookMailboxSettings
        {...chatSettings}
        onCancel={onBack}
        onSaveSuccess={() => {
          notify.success('Settings saved');
          onBack();
        }}
        cancelRef={backHandlerRef}
        className="px-5"
      />
    </div>
  );
}

function CountBadge({ count }: { count: number }) {
  return (
    <span
      className="inline-flex h-5 min-w-8 items-center justify-center rounded-full bg-[#E2EBF1] px-2 font-['Open_Sans',sans-serif] text-[12px] leading-5 font-semibold tracking-wider text-[#257FFB]"
      data-testid="outlook-settings-count-badge"
    >
      {count}
    </span>
  );
}

/**
 * Outlook Connect settings component that displays a toggle switch for enabling/disabling Outlook Connect.
 * Uses MSGraphToggle primitive to manage Outlook Connect state.
 * When connected, shows a summary with badge counts and a "Manage" link.
 */
export function OutlookConnectSettings() {
  const isMSGraphAuthEnabled = useFeatureFlag('nova-tools-graph-api', false);
  const { outlookConnectEnabled, serverSettings } = useChatOverlayContext();
  const { pushView, popView } = useSettingsPanelContext();

  if (!isMSGraphAuthEnabled) {
    return null;
  }

  const emailGroupCount = serverSettings?.outlookConnectedEmailGroupIds?.length ?? 0;
  const sharedInboxCount = serverSettings?.outlookSharedInboxes?.length ?? 0;

  return (
    <SettingsCard title="Outlook Connect" action={<MSGraphToggle />}>
      <div className="flex flex-col gap-2.5">
        <p className="font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
          Allow Nova to access your Outlook inbox
        </p>

        {outlookConnectEnabled && (
          <div className="flex items-start justify-between rounded-[6px] bg-[#FAFBFC] px-5 py-4">
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center gap-2.5">
                <CountBadge count={emailGroupCount} />
                <span className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#262D46]">
                  Email Groups
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <CountBadge count={sharedInboxCount} />
                <span className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#262D46]">
                  Shared inboxes
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => pushView(<OutlookManageView onBack={popView} />)}
              className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#0957C3]"
              data-testid="outlook-settings-manage"
            >
              Manage
            </button>
          </div>
        )}
      </div>
    </SettingsCard>
  );
}
